import { useAuthContext } from "../contexts/AuthContext";
import { useSubscription } from "./useSubscription";
import { useMemo } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useState, useEffect } from "react";

export type AccessBlockReason = 
  | "EMAIL_NOT_VERIFIED"
  | "SUBSCRIPTION_EXPIRED"
  | "PAYMENT_PENDING"
  | null;

export interface AccessControlState {
  hasAccess: boolean;
  blockReason: AccessBlockReason;
  blockMessage: string;
  isLoading: boolean;
}

/**
 * Hook สำหรับตรวจสอบสิทธิ์การเข้าถึงฟีเจอร์ต่างๆ
 * ตรวจสอบ 3 เงื่อนไข:
 * 1. ยืนยันอีเมลแล้ว
 * 2. แพ็คเก็จไม่หมดอายุ
 * 3. ไม่มีการชำระเงินรอการอนุมัติ
 */
export function useAccessControl() {
  const { user, emailVerified } = useAuthContext();
  const { tier } = useSubscription();
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(true);

  // ตรวจสอบว่ามีการชำระเงินรอการอนุมัติหรือไม่
  useEffect(() => {
    const checkPendingPayment = async () => {
      if (!user?.uid) {
        setCheckingPayment(false);
        setHasPendingPayment(false);
        return;
      }

      // Skip check for admin users
      if (user?.role === "admin") {
        setCheckingPayment(false);
        setHasPendingPayment(false);
        return;
      }

      try {
        // ตรวจสอบ subscription payments ที่รอการอนุมัติ
        const paymentsQuery = query(
          collection(db, "subscriptionPayments"),
          where("instructorId", "==", user.uid),
          where("status", "==", "pending")
        );
        const paymentsSnap = await getDocs(paymentsQuery);

        // ตรวจสอบ salepage payments ที่รอการอนุมัติ
        const salePagePaymentsQuery = query(
          collection(db, "salePagePayments"),
          where("instructorId", "==", user.uid),
          where("status", "==", "pending")
        );
        const salePagePaymentsSnap = await getDocs(salePagePaymentsQuery);

        const hasPending = !paymentsSnap.empty || !salePagePaymentsSnap.empty;
        setHasPendingPayment(hasPending);
      } catch (error: any) {
        // If permission error, assume no pending payments and continue
        console.warn("[useAccessControl] Could not check pending payments (permission denied or other error). Assuming no pending payments.", error?.code);
        setHasPendingPayment(false);
      } finally {
        setCheckingPayment(false);
      }
    };

    checkPendingPayment();
  }, [user?.uid, user?.role]);

  const accessState: AccessControlState = useMemo(() => {
    // Admin มีสิทธิ์เข้าถึงทุกอย่าง
    if (user?.role === "admin") {
      return {
        hasAccess: true,
        blockReason: null,
        blockMessage: "",
        isLoading: false,
      };
    }

    // ยังโหลดข้อมูลอยู่
    if (checkingPayment) {
      return {
        hasAccess: false,
        blockReason: null,
        blockMessage: "",
        isLoading: true,
      };
    }

    // เงื่อนไข 1: ต้องยืนยันอีเมล
    if (user?.role === "instructor" && !emailVerified) {
      return {
        hasAccess: false,
        blockReason: "EMAIL_NOT_VERIFIED",
        blockMessage: "กรุณายืนยันอีเมลก่อนใช้งานฟีเจอร์นี้",
        isLoading: false,
      };
    }

    // เงื่อนไข 3: มีการชำระเงินรอการอนุมัติ
    if (hasPendingPayment) {
      return {
        hasAccess: false,
        blockReason: "PAYMENT_PENDING",
        blockMessage: "มีการชำระเงินรอการอนุมัติจาก Admin กรุณารอสักครู่",
        isLoading: false,
      };
    }

    // เงื่อนไข 2: ตรวจสอบแพ็คเก็จหมดอายุ
    if (user?.subscriptionEndDate) {
      const now = new Date();
      const endDate = user.subscriptionEndDate instanceof Date 
        ? user.subscriptionEndDate 
        : (user.subscriptionEndDate as any).toDate();
      
      if (endDate < now) {
        return {
          hasAccess: false,
          blockReason: "SUBSCRIPTION_EXPIRED",
          blockMessage: "แพ็คเก็จของคุณหมดอายุแล้ว กรุณาต่ออายุเพื่อใช้งานต่อ",
          isLoading: false,
        };
      }
    }

    // ผ่านทุกเงื่อนไข
    return {
      hasAccess: true,
      blockReason: null,
      blockMessage: "",
      isLoading: false,
    };
  }, [user, emailVerified, tier, hasPendingPayment, checkingPayment]);

  return accessState;
}
